import apiClient from './api';

class ComplianceService {
  // ─── Licenses ───────────────────────────────────────────────────────────────
  async getLicenses() {
    try {
      const response = await apiClient.get('/compliance/licenses');
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch licenses.';
    }
  }

  async getLicense(id) {
    try {
      const response = await apiClient.get(`/compliance/licenses/${id}`);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch license.';
    }
  }

  async createLicense(dto) {
    try {
      const response = await apiClient.post('/compliance/licenses', dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to create license.';
    }
  }

  async updateLicense(id, dto) {
    try {
      const response = await apiClient.put(`/compliance/licenses/${id}`, dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to update license.';
    }
  }

  async deleteLicense(id) {
    try {
      await apiClient.delete(`/compliance/licenses/${id}`);
    } catch (error) {
      throw error.response?.data?.message || 'Failed to delete license.';
    }
  }

  async getExpiringLicenses(days = 30) {
    try {
      const response = await apiClient.get('/compliance/licenses/expiring-soon', { params: { days } });
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch expiring licenses.';
    }
  }

  // ─── Permits ────────────────────────────────────────────────────────────────
  async getPermits() {
    try {
      const response = await apiClient.get('/compliance/permits');
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch permits.';
    }
  }

  async getPermit(id) {
    try {
      const response = await apiClient.get(`/compliance/permits/${id}`);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch permit.';
    }
  }

  async createPermit(dto) {
    try {
      const response = await apiClient.post('/compliance/permits', dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to create permit.';
    }
  }

  async updatePermit(id, dto) {
    try {
      const response = await apiClient.put(`/compliance/permits/${id}`, dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to update permit.';
    }
  }

  async deletePermit(id) {
    try {
      await apiClient.delete(`/compliance/permits/${id}`);
    } catch (error) {
      throw error.response?.data?.message || 'Failed to delete permit.';
    }
  }

  async getExpiringPermits(days = 30) {
    try {
      const response = await apiClient.get('/compliance/permits/expiring-soon', { params: { days } });
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch expiring permits.';
    }
  }

  // ─── Products ───────────────────────────────────────────────────────────────
  async getProducts() {
    try {
      const response = await apiClient.get('/compliance/products');
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch products.';
    }
  }

  async getProduct(id) {
    try {
      const response = await apiClient.get(`/compliance/products/${id}`);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch product.';
    }
  }

  async createProduct(dto) {
    try {
      const response = await apiClient.post('/compliance/products', dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to create product.';
    }
  }

  async updateProduct(id, dto) {
    try {
      const response = await apiClient.put(`/compliance/products/${id}`, dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to update product.';
    }
  }

  async deleteProduct(id) {
    try {
      await apiClient.delete(`/compliance/products/${id}`);
    } catch (error) {
      throw error.response?.data?.message || 'Failed to delete product.';
    }
  }

  // ─── Product Categories ─────────────────────────────────────────────────────
  async getProductCategories() {
    try {
      const response = await apiClient.get('/compliance/product-categories');
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch product categories.';
    }
  }

  async createProductCategory(dto) {
    try {
      const response = await apiClient.post('/compliance/product-categories', dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to create product category.';
    }
  }

  // ─── Lab Tests ──────────────────────────────────────────────────────────────
  async getLabTests(batchId) {
    try {
      const response = await apiClient.get(`/compliance/labtests/batch/${batchId}`);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch lab tests.';
    }
  }

  async createLabTest(dto) {
    try {
      const response = await apiClient.post('/compliance/labtests', dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to create lab test.';
    }
  }

  // ─── Batches ─────────────────────────────────────────────────────────────────
  async getBatches(productId) {
    try {
      const response = await apiClient.get(`/compliance/batches/product/${productId}`);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch batches.';
    }
  }

  async createBatch(dto) {
    try {
      const response = await apiClient.post('/compliance/batches', dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to create batch.';
    }
  }

  // ─── Inventory Movement ─────────────────────────────────────────────────────
  async logInventoryMovement(dto) {
    try {
      const response = await apiClient.post('/compliance/inventory/movement', dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to log inventory movement.';
    }
  }

  // ─── Audits ──────────────────────────────────────────────────────────────────
  async getAudits(productId) {
    try {
      const response = await apiClient.get(`/compliance/audits/product/${productId}`);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch audits.';
    }
  }

  async createAudit(dto) {
    try {
      const response = await apiClient.post('/compliance/audits', dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to create audit.';
    }
  }

  // ─── Dashboard ───────────────────────────────────────────────────────────────
  async getComplianceDashboard() {
    try {
      const response = await apiClient.get('/compliance/dashboard');
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch compliance dashboard.';
    }
  }

  async getDashboard() {
    try {
      const response = await apiClient.get('/dashboard');
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch dashboard.';
    }
  }

  // ─── Documents ───────────────────────────────────────────────────────────────
  async uploadDocument(dto) {
    try {
      const response = await apiClient.post('/compliance/documents', dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to upload document.';
    }
  }
}

export default new ComplianceService();
